import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { SITE_ORIGIN, useSeo } from '@/lib/seo';
import { getContact } from '@/data/site';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Renders a CMS-managed legal/content page (cms_pages) fetched from the
// public content API. Body is a safe structured block list — no raw HTML.
export default function LegalPage({ slug }) {
  const [state, setState] = useState({ loading: true, page: null, notFound: false });
  const contact = getContact();
  const origin = SITE_ORIGIN;

  const page = state.page;

  useSeo({
    title: page?.seo?.title || 'Amden Group',
    description: page?.seo?.description || '',
    // Canonical comes from the CMS (production URL), never the preview origin
    canonical: page?.seo?.canonical || `${origin}/${slug}`,
    robots: page?.seo?.indexable === false ? 'noindex, nofollow' : undefined,
    jsonLd: page
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
            { '@type': 'ListItem', position: 2, name: page.name, item: `${origin}/${slug}` },
          ],
        }
      : undefined,
  });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, page: null, notFound: false });
    fetch(`${API}/public/pages/${slug}`)
      .then(async (res) => {
        if (res.status === 404) throw new Error('not-found');
        if (!res.ok) throw new Error('load-failed');
        return res.json();
      })
      .then((data) => alive && setState({ loading: false, page: data.page, notFound: false }))
      .catch((err) => alive && setState({ loading: false, page: null, notFound: err.message === 'not-found' }));
    return () => { alive = false; };
  }, [slug]);

  const updated = page?.updatedAt
    ? new Date(page.updatedAt).toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div data-testid={`legal-page-${slug}`}>
      <section className="border-b border-slate-200 bg-mist py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700" data-testid="legal-eyebrow">
            {page?.hero?.eyebrow || 'Legal'}
          </p>
          <h1 className="mt-3 text-balance text-4xl font-extrabold tracking-tight text-navy-900 sm:text-5xl" data-testid="legal-title">
            {page?.hero?.heading || page?.name || ''}
          </h1>
          {page?.hero?.copy && (
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{page.hero.copy}</p>
          )}
          {updated && (
            <p className="mt-5 font-mono text-xs font-medium tracking-widest text-slate-500" data-testid="legal-updated">
              Last updated: {updated}
            </p>
          )}
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          {state.loading && (
            <div className="flex justify-center py-16" role="status">
              <Loader2 className="h-6 w-6 animate-spin text-navy-900" />
              <span className="sr-only">Loading page</span>
            </div>
          )}

          {!state.loading && state.notFound && (
            <div className="rounded-xl border-2 border-dashed border-slate-300 bg-mist px-6 py-12 text-center" data-testid="legal-not-found">
              <p className="text-sm font-semibold text-slate-600">
                This page is being finalised. In the meantime, contact us at{' '}
                <a href={contact.emailHref} className="font-bold text-navy-900 underline-offset-2 hover:underline">{contact.email}</a>.
              </p>
              <Link to="/" className="mt-4 inline-block text-sm font-bold text-navy-900 underline-offset-2 hover:underline">
                Back to the homepage
              </Link>
            </div>
          )}

          {!state.loading && page && Array.isArray(page.body) && (
            <div className="space-y-6 text-base leading-relaxed text-slate-700" data-testid="legal-body">
              {page.body.map((block, i) => {
                if (block.type === 'h2')
                  return (
                    <h2 key={i} className="pt-6 text-2xl font-extrabold tracking-tight text-navy-900">
                      {block.text}
                    </h2>
                  );
                if (block.type === 'h3')
                  return (
                    <h3 key={i} className="pt-3 text-xl font-extrabold tracking-tight text-navy-900">
                      {block.text}
                    </h3>
                  );
                if (block.type === 'quote')
                  return (
                    <blockquote key={i} className="border-l-2 border-gold-500 pl-4 italic text-navy-800">
                      {block.text}
                    </blockquote>
                  );
                if (block.type === 'ul')
                  return (
                    <ul key={i} className="list-disc space-y-2 pl-6">
                      {(block.items || []).filter((li) => li.trim()).map((li, j) => (
                        <li key={j}>{li}</li>
                      ))}
                    </ul>
                  );
                return <p key={i}>{block.text}</p>;
              })}
            </div>
          )}

          {!state.loading && page && (
            <div className="mt-14 rounded-xl bg-navy-900 p-6 sm:p-8" data-testid="legal-contact-cta">
              <h2 className="text-lg font-extrabold tracking-tight text-white">Questions about this page?</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                Contact us at{' '}
                <a href={contact.emailHref} className="font-bold text-gold-500 underline-offset-2 hover:underline">{contact.email}</a>
                {' '}or call{' '}
                <a href={contact.phoneHref} className="font-bold text-gold-500 underline-offset-2 hover:underline">{contact.phone}</a>.
              </p>
              <Link
                to="/contact"
                data-testid="legal-contact-link"
                className="group mt-4 inline-flex items-center gap-2 rounded-md bg-gold-500 px-5 py-2.5 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-600"
              >
                Contact Amden Group
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
